<template>
  <div class="w-full flex px-4 pb-7" :style="{ height: `${barAreaHeight + 44}px` }">
    <!-- Left Axis Labels -->
    <div v-show="showLeftAxisLabels" class="h-full flex flex-col-reverse justify-between pr-3 flex-shrink-0">
      <BaseText v-for="label in leftAxisLabels" :key="`left-axis-label-${label}`"
      type="body" size="xs" class="text-text-subdued">
        {{ label }}
      </BaseText>
    </div>
    <!-- Graph Area -->
    <div class="relative h-full flex-grow flex items-end justify-between py-2"
    :style="{ paddingLeft: `${graphXPadding}px`, paddingRight: `${graphXPadding}px` }">
      <!-- Plot Lines -->
      <div class="absolute left-0 top-2 bottom-2 right-0 flex flex-col justify-between">
        <div v-for="n in numPlotLines" :key="`multi-plot-line-${n}`" class="w-full h-0 border-t border-border-normal"/>
      </div>
      <!-- Groups / Bars -->
      <div v-for="(group, index) in barDataGroups" :key="`multi-group-${index}`" class="relative flex items-end">
        <div v-for="(bar, barIndex) in group.bars" :key="`multi-bar-${index}-${bar.label}-${barIndex}`" 
        class="relative flex justify-center" :style="{ width: `${barSpacing}px` }">
          <!-- Bar -->
          <div class="w-2 graph-bar" style="border-radius: 3px; min-height: 2px;"
          :style="{'--bar-height': `${bar.height}px`, backgroundColor: barColors[barIndex]}" />
          <!-- Bar Label -->
          <div class="bar-label absolute -top-5 left-1/2 transform -translate-x-1/2">
            <BaseText type="label" size="xs" class="text-text-muted">
              {{ bar.label }}
            </BaseText>
          </div>
        </div>
        <!-- Group Label -->
        <div class="absolute -bottom-7 left-1/2 transform -translate-x-1/2 flex items-center justify-center gap-1.5 flex-nowrap whitespace-nowrap">
          <img v-if="group.label?.icon" :src="group.label.icon" class="w-4 h-4" />
          <BaseText type="body" size="xs" class="text-text-normal">
            {{ group.label.text }}
          </BaseText>
        </div>
      </div>
    </div>
    <!-- Right Axis Labels -->
    <div v-show="showRightAxisLabels" class="h-full flex flex-col-reverse justify-between pl-3 flex-shrink-0 text-right">
      <BaseText v-for="label in rightAxisLabels" :key="`right-axis-label-${label}`"
      type="body" size="xs" class="text-text-subdued">
        {{ label }}
      </BaseText>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { scaleLinear } from 'd3-scale'
import formatLabelString from '@/utils/lens/formatGraphLabelString'

const MAX_AXIS_LABELS = 6

export default {
  name: 'MultiMetricBreakdownBarGraph',
  props: {
    selectedMetrics: {
      type: Array,
      required: true
    },
    dataGroups: {
      type: Array,
      required: true
    },
    currencyRegion: {
      type: String,
      default: 'USD'
    },
    barColors: {
      type: Array,
      default: () => ['#FFAA51', '#00B78F']
    },
    barAreaHeight: {
      type: Number,
      default: 112
    },
    graphXPadding: {
      type: Number,
      default: 24
    },
    barSpacing: {
      type: Number,
      default: 40
    }
  },
  data () {
    return {
      axisScales: [],
      leftAxisLabels: [],
      rightAxisLabels: [],
      numPlotLines: 0
    }
  },
  watch: {
    dataGroups: {
      handler () {
        this.initializeAxisInformation()
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    ...mapGetters('MetricsModule', ['getMetricLookup']),
    barDataGroups () {
      if (!this.axisScales || !this.leftAxisLabels.length) return []
      return this.dataGroups.map(group => ({
        ...group,
        bars: group.values.map((value, index) => ({
          height: this.axisScales[index](value),
          label: this.formatAxisLabelString(value, index)
        }))
      }))
    },
    showLeftAxisLabels () {
      return this.selectedMetrics?.length >= 1 && this.selectedMetrics?.length <= 2
    },
    showRightAxisLabels () {
      return this.selectedMetrics?.length === 2
    }
  },
  methods: {
    initializeAxisInformation () {
      if (!this.selectedMetrics?.length || !this.dataGroups?.length) {
        this.axisScales = []
        this.leftAxisLabels = []
        this.rightAxisLabels = []
        return
      }
      this.computeAxisScales()
      this.computeLeftAxisLabels()
      this.computeRightAxisLabels()
    },
    computeAxisScales () {
      const scales = []
      const numMetrics = this.selectedMetrics.length
      if (numMetrics === this.dataGroups?.[0]?.values?.length) {
        for (let i = 0; i < numMetrics; i++) {
          const values = this.dataGroups.map(group => group.values[i])
          scales.push(this.createKpiAxisScale(values))
        }
      }
      this.axisScales = scales
    },
    computeLeftAxisLabels () {
      const scale = this.axisScales[0]
      let ticks = scale.ticks(MAX_AXIS_LABELS)
      const reduceTicks = (ticks) => {
        // Ensures that the number of ticks is less than or equal to 10
        let tickValues = [...ticks]
        if (ticks.length % 2 === 0) {
          const newMax = ticks[ticks.length - 1] + ticks[1]
          tickValues.push(newMax)
          scale.domain([0, newMax])
        }
        while (tickValues.length > MAX_AXIS_LABELS) {
          tickValues = tickValues.filter((_, index) => index % 2 === 0)
        }
        return tickValues
      }
      ticks = reduceTicks(ticks)
      this.leftAxisLabels = ticks.map(tick => this.formatAxisLabelString(tick, 0))
      this.numPlotLines = ticks.length
    },
    computeRightAxisLabels () {
      if (this.selectedMetrics.length < 2) {
        this.rightAxisLabels = []
        return
      }
      const numLabels = this.leftAxisLabels.length
      const scale = this.axisScales[1]
      const [_, d1] = scale.domain()
      const maxValue = d1

      const step = maxValue / (numLabels - 1)
      const ticks = Array.from({ length: numLabels }, (_, index) => index * step)
      this.rightAxisLabels = ticks.map(tick => this.formatAxisLabelString(tick, 1))
    },
    formatAxisLabelString (value, selectedKpiIndex) {
      const kpi = this.selectedMetrics[selectedKpiIndex]
      const type = this.getMetricLookup?.[kpi]?.type
      return formatLabelString(value, type, this.currencyRegion)
    },
    createKpiAxisScale (values) {
      let maxValue = Math.max(...values)
      if (maxValue === 0) maxValue = 1
      const scale = scaleLinear()
        .domain([0, maxValue])
        .range([0, this.barAreaHeight])
        .nice()
      return scale
    }
  }
}
</script>

<style scoped>
.graph-bar {
  height: var(--bar-height);
  animation: barEnter 0.75s ease-out forwards;
}
@keyframes barEnter {
  from { height: 0px; }
  to { height: var(--bar-height); }
}
.bar-label {
  z-index: 5;
  text-shadow:
    -1px -1px 0 #fff,
     1px -1px 0 #fff,
    -1px  1px 0 #fff,
     1px  1px 0 #fff;
}
</style>